import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Card, Typography, Row, Col, Input, Tag, Tooltip, message } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { authAxios } from '../utils/session_utils';
import { StreamingChannel, YouTubeLiveStream } from './types';
import ChannelForm from './ChannelForm';
import { ComponentTitle } from '../custom-components';

const { Text } = Typography;
const { Search } = Input;

/**
 * Channels: the permanent identity of a feed, independent of the stream carrying
 * it. The id published here is what partners key their ingest configuration to,
 * so there is deliberately no delete and no way to change an id — a channel that
 * is no longer used is simply left unassigned.
 */
const ChannelManager: React.FC = () => {
  const [channels, setChannels] = useState<StreamingChannel[]>([]);
  const [streams, setStreams] = useState<YouTubeLiveStream[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<StreamingChannel | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [searchText, setSearchText] = useState('');

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      // Streams come along so the table can show what each channel is carrying —
      // the thing an operator wants to know before renaming one.
      const [channelRes, streamRes] = await Promise.all([
        authAxios({ method: 'GET', url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/channels` }),
        authAxios({ method: 'GET', url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams` }),
      ]);
      setChannels(channelRes?.data?.channels ?? []);
      setStreams(streamRes?.data?.streams ?? []);
    } catch (error) {
      console.error('Error loading channels:', error);
      message.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const handleCreate = () => {
    setSelectedChannel(null);
    setShowForm(true);
  };

  const handleEdit = (channel: StreamingChannel) => {
    setSelectedChannel(channel);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    setSelectedChannel(null);
    loadChannels();
  };

  const streamsFor = (channelId: string) => streams.filter((s) => s.channel_id === channelId);

  const filtered = channels.filter((c) => {
    const q = searchText.trim().toLowerCase();
    if (!q) return true;
    return (
      c.id.toLowerCase().includes(q) ||
      c.display_name.toLowerCase().includes(q) ||
      (c.language ?? '').toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Channel ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <Tooltip title="Permanent — published in the EPG and used by partners to identify this channel.">
          <Text code>{id}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: (lang?: string) =>
        lang ? <Tag>{lang}</Tag> : <Text type="secondary">English (default)</Text>,
    },
    {
      title: 'Logo',
      dataIndex: 'logo_url',
      key: 'logo_url',
      render: (url?: string) =>
        url ? (
          <a href={url} target="_blank" rel="noreferrer">
            View
          </a>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'EPG Profile',
      dataIndex: 'epg_profile',
      key: 'epg_profile',
      render: (profile?: string | null) =>
        profile === 'amazon' ? <Tag color="orange">Amazon</Tag> : <Text type="secondary">Default</Text>,
    },
    {
      title: 'Used By',
      key: 'used_by',
      render: (_: unknown, channel: StreamingChannel) => {
        const used = streamsFor(channel.id);
        if (used.length === 0) return <Text type="secondary">Not assigned</Text>;
        return (
          <Space direction="vertical" size={0}>
            {used.map((s) => (
              <Text key={s.id}>{s.name}</Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, channel: StreamingChannel) => (
        <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(channel)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
        <Col>
          <ComponentTitle>Channels</ComponentTitle>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add Channel
          </Button>
        </Col>
      </Row>

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={18}>
            <Search
              placeholder="Search channels by name, id, or language..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Button onClick={loadChannels} style={{ width: '100%' }}>
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} channels`,
          }}
        />
      </Card>

      <ChannelForm
        channel={selectedChannel}
        open={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSaved}
      />
    </div>
  );
};

export default ChannelManager;
